import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Flex from 'components/common/Flex';
import { Link, useParams } from 'react-router-dom';
import { Button, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { ProductContext } from 'context/Context';
import useProductHook from './useProductHook';
import ProductImage from './ProductImage';
import StarRating from 'components/common/StarRating';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation, Trans } from 'react-i18next';

const ProductGrid = ({ product, ...rest }) => {
  const { t } = useTranslation('commerce');
  const {
    name,
    category,
    id,
    price,
    salePrice,
    shippingCost,
    rating,
    totalReview,
    isInStock,
    stock,
    isNew,
    files
  } = product;

  const {
    isInFavouriteItems,
    productsState: { config }
  } = useContext(ProductContext);
  const currency = config.currency;
  const params = useParams();
  const { handleAddToCart, handleFavouriteClick } = useProductHook(product);
  return (
    <Col className="mb-4" {...rest}>
      <Flex
        direction="column"
        justifyContent="between"
        className="border rounded-1 h-100 pb-3"
      >
        <div className="overflow-hidden">
          <ProductImage
            name={name}
            id={id}
            isNew={isNew}
            files={files}
            layout="grid"
          />
          <hr className="m-0" />
          <div className="p-3">
            <h5 className="fs-0">
              <Link
                className="text-dark"
                to={`/${params.corp}/e-commerce/product/product-details/${id}`}
              >
                {name}
              </Link>
            </h5>
            <p className="fs--1 mb-3">
              <Link to="#!" className="text-500">
                {category}
              </Link>
            </p>
            <h5 className="fs-md-2 text-warning mb-0 d-flex align-items-center mb-3">
              {`${currency}${salePrice ? salePrice : price}`}
              {salePrice ? (
                <del className="ms-2 fs--1 text-500">{`${currency}${price}`}</del>
              ) : null}
            </h5>
            {shippingCost ? (
              <p className="fs--1 mb-1">
                {isNaN(shippingCost) ? (
                  <strong>{shippingCost}</strong>
                ) : (
                  <>
                    <Trans ns="commerce">Shipping Cost</Trans> :{' '}
                    <strong>{`${currency}${shippingCost}`}</strong>
                  </>
                )}
              </p>
            ) : null}
            <p className="fs--1 mb-1">
              <Trans ns="commerce">Stock</Trans> :{' '}
              <strong
                className={classNames({
                  'text-success': isInStock,
                  'text-danger': !isInStock
                })}
              >
                {stock ? stock : isInStock ? t('Available') : t('Sold-Out')}
              </strong>
            </p>
          </div>
        </div>
        <Flex alignItems="center" className="px-3">
          <div className="fs--2 flex-1">
            <StarRating readonly rating={rating} />
            {config.withReview ? (
              <span className="ms-1">({totalReview})</span>
            ) : null}
          </div>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                <Trans ns="commerce">Favourite</Trans>
              </Tooltip>
            }
          >
            <Button
              disabled={!config.withFavorite}
              variant="falcon-default"
              size="sm"
              onClick={handleFavouriteClick}
              className="me-2"
            >
              <FontAwesomeIcon
                icon={isInFavouriteItems(id) ? 'heart' : ['far', 'heart']}
              />
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                <Trans ns="commerce">Add to Cart</Trans>
              </Tooltip>
            }
          >
            <Button
              disabled={!config.withCart}
              variant="falcon-default"
              size="sm"
              onClick={() => handleAddToCart(1, true, true)}
            >
              <FontAwesomeIcon icon="cart-plus" />
            </Button>
          </OverlayTrigger>
        </Flex>
      </Flex>
    </Col>
  );
};

ProductGrid.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string.isRequired,
    category: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    features: PropTypes.array,
    price: PropTypes.number.isRequired,
    discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    salePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shippingCost: PropTypes.any,
    rating: PropTypes.number,
    totalReview: PropTypes.any,
    isInStock: PropTypes.any,
    stock: PropTypes.string,
    isNew: PropTypes.any,
    files: PropTypes.arrayOf(PropTypes.string)
  })
};

export default ProductGrid;
